/* Markier-Modus der Kaderliste (Singleton, nur im Speicher).
 *
 * Nichts wird gespeichert: keine Datenbank, kein localStorage. Beim Neuladen ist die Markierung weg.
 * Gespeichert werden Spieler-IDs, nicht Objekte — so bleibt die Markierung beim Wechsel von Filter,
 * Suche und Jahrgang erhalten, und gelöschte oder verschobene Spieler fallen über resolve() heraus.
 */

import { ref, computed } from 'vue';
import { useAuth } from './useAuth.js';
import { canSelectPlayers, resolveSelection } from '../domain/selection.js';

const selecting = ref(false);
/* Set wird beim Schreiben ersetzt, nicht verändert — so bleibt es reaktiv, ohne reactive(Set) */
const ids = ref(new Set());

export function useSelection() {
  const { role } = useAuth();

  const canSelect = computed(() => canSelectPlayers(role?.value));

  /** Die markierten Spieler, die in der übergebenen Liste (geladene Spieler) noch im Kader stehen. */
  const resolve = (players) => resolveSelection(ids.value, players);
  const isSelected = (id) => ids.value.has(id);

  /** Modus einschalten, optional gleich ein Profil markieren. Ohne Recht passiert nichts. */
  function enter(id = null) {
    if (!canSelectPlayers(role?.value)) return false;
    selecting.value = true;
    if (id != null && !ids.value.has(id)) ids.value = new Set([...ids.value, id]);
    return true;
  }

  function toggle(id) {
    if (!selecting.value || id == null) return;
    const next = new Set(ids.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    ids.value = next;
  }

  /** Genau dieses Profil markieren (nicht umschalten). */
  function select(id) {
    if (selecting.value && id != null && !ids.value.has(id)) ids.value = new Set([...ids.value, id]);
  }

  /** Alle übergebenen IDs markieren (z. B. alles, was nach Filter/Suche/Jahrgang sichtbar ist). */
  function selectAll(list) {
    if (!selecting.value) return;
    ids.value = new Set([...ids.value, ...list]);
  }

  function clear() {
    ids.value = new Set();
  }

  /** Modus beenden und Markierung löschen. */
  function done() {
    selecting.value = false;
    ids.value = new Set();
  }

  return { selecting, canSelect, ids, resolve, isSelected, enter, toggle, select, selectAll, clear, done };
}
